/** Keys for form-link CTA buttons (stored in DB as {{key}} or {{key:label}} or {{key::color}} or {{key:label:color}}). */
export type FormLinkTemplateButtonKey = "form_link_invite_button" | "form_link_view_edit_button";

export const FORM_LINK_TEMPLATE_BUTTONS: Record<
  FormLinkTemplateButtonKey,
  { defaultText: string; placeholder: string; href: string }
> = {
  form_link_invite_button: {
    defaultText: "Fill Out Form",
    placeholder: "{{form_link_invite_button}}",
    href: "{{form_link}}",
  },
  form_link_view_edit_button: {
    defaultText: "View or Edit Submission",
    placeholder: "{{form_link_view_edit_button}}",
    href: "{{form_link}}",
  },
};

/** @deprecated Use FORM_LINK_TEMPLATE_BUTTONS — kept for variable dropdown labels */
export const TEMPLATE_BUTTON_VARIABLES = Object.fromEntries(
  Object.entries(FORM_LINK_TEMPLATE_BUTTONS).map(([key, config]) => [
    config.placeholder,
    { text: config.defaultText, href: config.href, templateKey: key as FormLinkTemplateButtonKey },
  ])
) as Record<string, { text: string; href: string; templateKey: FormLinkTemplateButtonKey }>;

const FORM_LINK_HREF = "{{form_link}}";
const DEFAULT_BUTTON_COLOR = "#5261EB";

const isTemplateHref = (href: string) => href.includes("{{");

const isHexColor = (value: string): boolean => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value.trim());

/** Wraps inner placeholder content with `{{` / `}}` (avoid `{{${x}}::` template-literal brace bugs). */
const wrapPlaceholder = (inner: string): string => `{{${inner}}}`;

/**
 * Fixes `{{form_link_*_button}::#color}}` → `{{form_link_*_button::#color}}` from a prior serialization bug.
 */
export const repairMalformedFormLinkPlaceholders = (html: string): string => {
  let result = html;
  for (const templateKey of Object.keys(FORM_LINK_TEMPLATE_BUTTONS) as FormLinkTemplateButtonKey[]) {
    result = result.replace(new RegExp(`\\{\\{${templateKey}\\}(::[^}]+)\\}\\}`, "g"), `{{${templateKey}$1}}`);
  }
  return result;
};

/**
 * Parses optional segments from a form-link button placeholder.
 * Supports {{key}}, {{key:label}}, {{key::color}}, and {{key:label:color}}.
 */
export const parseFormLinkButtonPlaceholderSegments = (
  segment1?: string,
  segment2?: string
): { label?: string; color?: string } => {
  const first = segment1?.trim() ?? "";
  const second = segment2?.trim() ?? "";

  if (!first && !second) return {};
  if (!first && second) return { color: second };
  if (first && !second) {
    if (isHexColor(first)) return { color: first };
    return { label: first };
  }
  return { label: first, color: second };
};

const formLinkPlaceholderRegex = (templateKey: FormLinkTemplateButtonKey): RegExp =>
  new RegExp(`\\{\\{${templateKey}(?::([^:}]*))?(?::([^}]*))?\\}\\}`, "g");

/**
 * Serializes a form-link button to its template placeholder (optional custom label and color).
 *
 * @param templateKey - Which form-link button type
 * @param text - Button label shown in the email
 * @param color - Button background color (hex)
 * @param defaultColor - Branding default; omitted from placeholder when unchanged
 * @returns Placeholder string for storage
 */
export const formatFormLinkButtonPlaceholder = (
  templateKey: FormLinkTemplateButtonKey,
  text: string,
  color?: string,
  defaultColor: string = DEFAULT_BUTTON_COLOR
): string => {
  const trimmedText = text.trim();
  const trimmedColor = color?.trim();
  const { defaultText } = FORM_LINK_TEMPLATE_BUTTONS[templateKey];

  const isDefaultText = !trimmedText || trimmedText === defaultText;
  const isDefaultColor = !trimmedColor || trimmedColor.toLowerCase() === defaultColor.trim().toLowerCase();

  if (isDefaultText && isDefaultColor) return wrapPlaceholder(templateKey);
  if (isDefaultText && trimmedColor) return wrapPlaceholder(`${templateKey}::${trimmedColor}`);
  if (!isDefaultText && isDefaultColor) return wrapPlaceholder(`${templateKey}:${trimmedText}`);
  return wrapPlaceholder(`${templateKey}:${trimmedText}:${trimmedColor}`);
};

/**
 * Builds TipTap button-block HTML for a template CTA (preserves {{form_link}} in href).
 */
export const buildTemplateButtonHtml = (
  text: string,
  href: string,
  color: string,
  templateKey?: FormLinkTemplateButtonKey
): string => {
  const safeHref = isTemplateHref(href) ? href : href.startsWith("http") ? href : `https://${href}`;
  const templateAttr = templateKey ? ` data-template-key="${templateKey}"` : "";
  return `<div style="text-align:center; margin:20px 0;"><a data-type="button"${templateAttr} href="${safeHref}" style="display:inline-block; padding:12px 24px; background-color:${color}; color:#ffffff; text-decoration:none; border-radius:6px; font-weight:600; font-size:15px;">${text}</a></div>`;
};

const extractButtonBackgroundColor = (anchorAttrs: string): string | undefined => {
  const match = anchorAttrs.match(/background-color:\s*([^;"']+)/i);
  return match?.[1]?.trim();
};

/**
 * Converts stored template placeholders into editor-friendly button blocks.
 */
export const placeholdersToEditorHtml = (html: string, defaultColor = DEFAULT_BUTTON_COLOR): string => {
  let result = repairMalformedFormLinkPlaceholders(html);
  for (const [templateKey, config] of Object.entries(FORM_LINK_TEMPLATE_BUTTONS) as [
    FormLinkTemplateButtonKey,
    (typeof FORM_LINK_TEMPLATE_BUTTONS)[FormLinkTemplateButtonKey],
  ][]) {
    result = result.replace(formLinkPlaceholderRegex(templateKey), (_match, seg1?: string, seg2?: string) => {
      const { label, color } = parseFormLinkButtonPlaceholderSegments(seg1, seg2);
      const text = label || config.defaultText;
      const btnColor = color || defaultColor;
      return buildTemplateButtonHtml(text, config.href, btnColor, templateKey);
    });
  }
  return result;
};

/**
 * Converts editor button blocks back to template placeholders for storage.
 */
export const editorHtmlToPlaceholders = (html: string, defaultColor = DEFAULT_BUTTON_COLOR): string => {
  const buttonBlockRegex = /<a([^>]*data-type="button"[^>]*)>([^<]*)<\/a>/gi;

  return repairMalformedFormLinkPlaceholders(html).replace(
    buttonBlockRegex,
    (_match, anchorAttrs: string, rawText: string) => {
      const trimmedText = rawText.trim();
      const hrefMatch = anchorAttrs.match(/href="([^"]*)"/i);
      const href = hrefMatch?.[1] ?? "";
      const normalizedHref = href.replace(/^https:\/\//, "");
      const isFormLink =
        normalizedHref === FORM_LINK_HREF || href === FORM_LINK_HREF || normalizedHref.includes("{{form_link}}");

      if (!isFormLink) {
        return _match;
      }

      const buttonColor = extractButtonBackgroundColor(anchorAttrs);
      const templateKeyMatch = anchorAttrs.match(/data-template-key="([^"]*)"/i);
      const templateKey = templateKeyMatch?.[1] as FormLinkTemplateButtonKey | undefined;

      if (templateKey && templateKey in FORM_LINK_TEMPLATE_BUTTONS) {
        return formatFormLinkButtonPlaceholder(templateKey, trimmedText, buttonColor, defaultColor);
      }

      for (const [key, config] of Object.entries(FORM_LINK_TEMPLATE_BUTTONS) as [
        FormLinkTemplateButtonKey,
        (typeof FORM_LINK_TEMPLATE_BUTTONS)[FormLinkTemplateButtonKey],
      ][]) {
        if (trimmedText === config.defaultText) {
          return formatFormLinkButtonPlaceholder(key, trimmedText, buttonColor, defaultColor);
        }
      }

      return _match;
    }
  );
};

/** Human-readable labels for the variables dropdown. */
export const VARIABLE_DISPLAY_LABELS: Record<string, string> = {
  "{{first_name}}": "First name",
  "{{full_name}}": "Full name",
  "{{email}}": "Email",
  "{{form_name}}": "Form name",
  "{{form_link}}": "Form link (URL)",
  "{{form_link_invite_button}}": "Form link button",
  "{{form_link_view_edit_button}}": "View / edit submission button",
  "{{event_location}}": "Event location",
  "{{checkin_date}}": "Check-in date",
  "{{checkout_date}}": "Check-out date",
  "{{seat_assignment}}": "Seat assignment",
  "{{hotel_confirmation}}": "Hotel confirmation #",
  "{{hotel_name}}": "Hotel name",
  "{{qr_code}}": "QR code",
  "{{barcode}}": "Barcode",
  "{{signoff}}": "Sign-off block",
};
